import { Action, createBrowserHistory, Location } from 'history';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import StorageUtil from '../utils/StorageUtils';
import StoreParaSingleton from './StoreParaSingleton';

export const FECHAR_ESPELHOS_PROCESSO_ABA_GE_KEY = 'fechar-espelhos-processo-aba-ge';
export const PARAM_ESPELHO_DEPENDENTE = 'dependente';

function patchHistoryWithExtras(history: any) {
   history.createURL = (location: Location) => {
      return typeof location === 'string'
         ? location
         : history.createHref(location);
   };
   history.encodeLocation = (location: Location) => location;
   return history;
}

class RouterStore extends StoreParaSingleton {
   history = patchHistoryWithExtras(createBrowserHistory());
   location; //= this.history.location;
   action: Action;// = this.history.action;

   lastRoute;
   rotasAnteriores: string[] = [];
   params: any;
   routerStore = null;
   erroCarregamentoEspelho = false;
   dadosProcessoEspelho = null;
   processoImportacaoEspelho = null;
   janelasEspelhoProcesso: Window[] = [];

   constructor() {
      super();

      makeObservable(this, {
         location: observable,
         action: observable,
         lastRoute: observable,
         rotasAnteriores: observable,
         params: observable,
         routerStore: observable,
         erroCarregamentoEspelho: observable,
         dadosProcessoEspelho: observable,
         processoImportacaoEspelho: observable,
         inicializar: action,
         objetoSave: computed,
         setErroCarregamentoEspelho: action,
         setDadosProcessoEspelho: action,
         setProcessoImportacaoEspelho: action,
         setParams: action,
         push: action,
         removerParametroQuery: action,
         abrirJanela: action,
         rotaAtual: computed,
         isAM: computed,
         rotaAtualEscaninho: computed,
         isRotaResumoDocumentosAguia: computed,
         isRotaIndependente: computed
      });

      runInAction(() => {
         this.location = this.history.location;
         this.action = this.history.action;
      });

      this.history.listen((update) => {
         runInAction(() => {
            this.location = update.location;
            this.action = update.action;
            this.lastRoute = update.location.pathname;
         });
      });

      const storage = StorageUtil.getObject(this.nomeStorage);
      if (storage) {
         for (const key in this.objetoSave) {
            this[key] = storage[key];
         }
      }

      // Keep lastRoute aligned with the URL even on direct access/reload.
      this.lastRoute = this.history.location.pathname;
   }

   public inicializar(router?) {
      if (router) {
         this.routerStore = router;
      }

      super.inicializar();
   }

   get objetoSave() {
      return {
         nomeStore: 'RouterStore',
         lastRoute: this.lastRoute,
         rotaAnterior: this.rotasAnteriores || [],
         params: this.params
      };
   }


   setParams(params: any) {
      this.params = params;
   }

   setErroCarregamentoEspelho(erro: boolean) {
      this.erroCarregamentoEspelho = erro;
   }

   setDadosProcessoEspelho(dadosProcesso) {
      this.dadosProcessoEspelho = dadosProcesso;
   }

   setProcessoImportacaoEspelho(processo) {
      this.processoImportacaoEspelho = processo;
   }

   push(route: string) {
      this.rotasAnteriores.unshift(this.lastRoute);
      this.lastRoute = route;
      this.history.push(route);
   }

   removerParametroQuery(parametro: string) {
      const location = this.location || this.history.location;
      const parametros = new URLSearchParams(location.search);

      if (!parametros.has(parametro)) {
         return;
      }

      parametros.delete(parametro);
      const search = parametros.toString();
      this.history.replace({
         pathname: location.pathname,
         search: search ? `?${search}` : '',
         hash: location.hash
      });
   }

   propagarDependenciaEspelho = (endereco: string, deveRastrearEspelho = false) => {
      const abrindoEspelhoProcesso = endereco.startsWith('/espelhoProcesso/');
      const devePropagarDependencia = abrindoEspelhoProcesso && window.location.search.includes(PARAM_ESPELHO_DEPENDENTE + '=true');
      if ((deveRastrearEspelho || devePropagarDependencia) && !endereco.includes(PARAM_ESPELHO_DEPENDENTE + '=')) {
         endereco += (endereco.includes('?') ? '&' : '?') + PARAM_ESPELHO_DEPENDENTE + '=true';
      }

      return endereco;
   }

   abrirJanela = (endereco: string, ...parametrosEmOrdem: string[]) => {
      parametrosEmOrdem.forEach(param => {
         endereco += '/' + param;
      });
      const abrindoEspelhoProcesso = endereco.startsWith('/espelhoProcesso/');
      const deveRastrearEspelho = !this.isRotaEspelho && abrindoEspelhoProcesso;
      endereco = this.propagarDependenciaEspelho(endereco, deveRastrearEspelho);
      const janela = window.open(endereco, '_blank');
      if (janela && deveRastrearEspelho) {
         this.janelasEspelhoProcesso.push(janela);
      }
      return janela;
   };

   limparJanelasEspelhoFechadas = () => {
      this.janelasEspelhoProcesso = this.janelasEspelhoProcesso.filter(janela => janela && !janela.closed);
   }

   get possuiJanelasEspelhoAbertas() {
      this.limparJanelasEspelhoFechadas();
      return this.janelasEspelhoProcesso.length > 0;
   }

   fecharJanelasEspelhoAbertas = () => {
      this.limparJanelasEspelhoFechadas();
      localStorage.setItem(FECHAR_ESPELHOS_PROCESSO_ABA_GE_KEY, String(Date.now()));
      this.janelasEspelhoProcesso.forEach(janela => {
         try {
            janela.close();
         } catch (erro) {
            console.error('Erro ao fechar aba filha do espelho do processo:', erro);
         }
      });
      this.janelasEspelhoProcesso = [];
   }

   redirecionamentoExterno = (endereco: string) => {
      endereco = this.propagarDependenciaEspelho(endereco);
      window.location.href = endereco;
   };

   getParam(param: string): string[] {
      const search = this.location?.search || window.location.search;
      let valor: string[] = [];

      try {
         valor = search.substring(1)
            .split('&')
            .filter(c => c.includes(param))
            .map(c => c.split('=')[1]);
      } catch (_e) {
         throw new Error(`Erro: Não foi possível carregar o parâmetro ${param}`);
      }

      return valor;
   }

   get isImpressao() {
      return this.rotaAtual && (this.rotaAtual.indexOf('/impressaoEspelhoProcesso') > -1);
   }

   get isRotaEspelho() {
      const rota = this.rotaAtual || window.location.pathname;
      return rota?.startsWith('/espelhoProcesso/')
         || rota?.startsWith('/impressaoEspelhoProcesso/');
   }

   get isRotaResumoDocumentosAguia() {
      const rota = this.rotaAtual || window.location.pathname;
      return rota?.startsWith('/resumoDocumentosAguia/');
   }

   get isRotaIndependente() {
      return this.isRotaEspelho || this.isRotaResumoDocumentosAguia;
   }

   get isEscaninhoAssinatura() {
      return this.rotaAtual?.includes('/escaninho/assinatura') || this.rotaAtual?.includes('/escaninho/assinaturaNova');
   }

   get isEscaninhoFiltrado() {
      return this.rotaAtual?.includes('/escaninho/filtrado');
   }

   get isEscaninhoJulgamento() {
      return this.rotaAtual?.includes('/escaninho/julgamento');
   }

   get isEscaninhoPauta() {
      return this.rotaAtual?.includes('/escaninho/pauta') || window.location.href.includes('/escaninho/pauta');
   }

   get isEscaninhoUsuario() {
      return this.rotaAtual?.includes('/escaninho/usuario') || window.location.href.includes('/escaninho/usuario');
   }

   get isControleGabinete() {
      return this.rotaAtual?.includes('/controleGabinete') || window.location.href.includes('/controleGabinete');
   }

   get isEscaninhoGerencial() {
      return this.rotaAtual?.includes('/escaninho/gerencial') || window.location.href.includes('/escaninho/gerencial');
   }

   get isEscaninhoDeVistos() {
      return this.rotaAtual?.includes('/escaninho/vistos') || window.location.href.includes('/escaninho/vistos');
   }

   get isEscaninhoDeVotoVencido() {
      return this.rotaAtual?.includes('/escaninho/voto-vencido') || window.location.href.includes('/escaninho/voto-vencido');
   }
   get isEscaninhoDeRedatorDesignado() {
      return this.rotaAtual?.includes('/escaninho/redator-designado') || window.location.href.includes('/escaninho/redator-designado');
   }

   get isImportacaoNoEscaninho() {
      return this.isEscaninhoGerencial && this.getParam('codImportacao').length > 0;
   }

   get isEscaninhoPeticao() {
      return this.rotaAtual?.includes('/escaninho/peticao') || window.location.href.includes('/escaninho/peticao');
   }

   get isAlgumEscaninho() {
      return this.rotaAtual?.includes('/escaninho/') || window.location.href.includes('/escaninho/');
   }

   get isMinutasAssinadas() {
      return this.rotaAtual?.includes('/minutasAssinadas') || window.location.href.includes('/minutasAssinadas');
   }

   get isAmbienteHomologacaoOuDev() {
      const url = window.location.href
      return url.includes('hml') || url.includes('localhost') || url.includes('dev');
   }

   get rotaAtualEscaninho() {
      const rota = this.rotaAtual || window.location.pathname;
      const pathEscaninho = '/escaninho/';

      if (rota?.includes(pathEscaninho)) {
         const startIndex = rota.indexOf(pathEscaninho) + pathEscaninho.length;
         return rota.slice(startIndex).split('?')[0].split('/')[0];
      }

      return '';
   }

   get rotaAtual() {
      return this.location?.pathname;
   }

   get isAM() {
      return (this.rotaAtual && this.rotaAtual.includes('/assistenteMinutas')) || window.location.href.includes('/assistenteMinutas/');
   }

   irParaTelaPrincipalAm = () => {
      this.push('/assistenteMinutas');
   }

   irParaEscaninhoAssinatura = () => {
      this.push('/escaninho/assinatura');
   }

   irParaEscaninhoPauta = () => {
      this.push('/escaninho/pauta');
   }

   get isEspelho() {
      const rota = this.rotaAtual || window.location.pathname;
      return rota?.startsWith('/espelhoProcesso/');
   }

   get isHml() {
      return window.location.href.includes('.hml.') || window.location.href.includes('.dev.') || window.location.href.includes(':3000');
   }

   get isPrd() {
      return window.location.href.includes('.prd.') || window.location.href.includes('.bet.') || window.location.href.includes('.tst.jus.br');
   }

   limpar = () => {
      this.lastRoute = '/';
      this.rotasAnteriores = [];
      StorageUtil.removeItem(this.nomeStorage);
   }
}

export default new RouterStore();
